/**
 * drawerSchema.js — arg/type shapes only, NO component imports.
 *
 * Split from `drawerRegistry.js` to break a circular import:
 *   drawerRegistry → SiteDetailLayer → SiteDetails → layerNav → drawerParser → drawerRegistry
 * The parser only needs the schema (arg names + coercion types) to validate
 * and normalise URL segments, not the component references. Component
 * resolution stays in `drawerRegistry.js` and is consumed only by
 * `DrawerStack.vue`.
 */

export const drawerSchema = Object.freeze({
  site: {
    args: ['id', 'tab', 'subtab', 'subsubtab'],
    types: { id: 'int', tab: 'string', subtab: 'string', subsubtab: 'string' }
  },
  // L1 view from /utility: a site's utility-bill LIST (legacy
  // /utility/:utilityId → SiteUtilTable). Chevron from /utility row should
  // open this, not jump straight to a single bill.
  siteUtil: {
    args: ['id', 'tab', 'subtab'],
    types: { id: 'int', tab: 'string', subtab: 'string' }
  },
  // L2 single bill detail (legacy /utility/:utilityId/bill/:billId →
  // SiteUtilBillDetail). Reached from inside a `siteUtil` or `site` (Power
  // tab) drawer, never directly from a list.
  bill: {
    args: ['id', 'tab', 'subtab'],
    types: { id: 'int', tab: 'string', subtab: 'string' }
  },
  // L1 request detail (XOL-209 PR 3). Opened from /requests/<typeSlug>.
  // tab/subtab reserved for future sub-tab routing — not wired in this PR.
  // XOL-67 (B3, v2.1 V1): `step` (1 to 5) is the step an RTA drawer (RtaDetails) shows. 'optint': absent =
  // null, so every d.request.<id> link that predates it still parses; the other request types
  // ignore it. A legacy `tab` on an RTA is read once and turned into a step. v2.1 has five steps again (decision
  // 154: Requested, Searching, Client Review, Tower Co Decision, Finalize): `bounds` clamps the parsed value, so a
  // step above 5 reads as 5, the last step, and a step below 1 reads as absent.
  request: {
    args: ['id', 'tab', 'subtab', 'step'],
    types: { id: 'int', tab: 'string', subtab: 'string', step: 'optint' },
    bounds: { step: { min: 1, max: 5 } }
  },
  // L2 e-mail download landing (XOL-650), keyed by file id; `via` is the e-mail source
  // flag (d.file.9.email). Opened by the Download link in request e-mails, stacked on the
  // request drawer: /requests/d.request.42/d.file.9.email.
  file: {
    args: ['id', 'via'],
    types: { id: 'int', via: 'string' }
  },
  // L1 read-only request-report timeline (XOL-209 — /requests/summary).
  // Keyed by action_id + partyNo (one summary row per party per action).
  requestReport: {
    args: ['id', 'partyNo'],
    types: { id: 'int', partyNo: 'int' }
  },
  // L1 backhaul provider detail (XOL-230 PR-A). Opened from /backhaul or
  // embedded BackhaulTable (provider=true). siteId is the numeric site FK.
  backhaulProvider: {
    args: ['id', 'siteId'],
    types: { id: 'int', siteId: 'int' }
  },
  // L1 backhaul customer detail. Opened from /backhaul subscribers view or
  // embedded BackhaulTable (provider=false). Keyed by id only (XOL-264) —
  // GET /backhaul/subscribers/{id} returns the record with site + org appended,
  // so siteId/sharerId no longer need to live in the URL.
  backhaulCustomer: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 ticket detail (XOL-230 PR-B). Opened from /tickets or /dashboard/maintenance.
  // Keyed by ticket id only — GET /tickets/{id} returns the full ticket (site +
  // asset appended), so the DetailLayer needs no siteId/assetId in the URL. (The
  // parser treats every int arg as required, so optional numeric FKs can't live
  // here anyway — an asset-less ticket would drop the whole entry.)
  ticket: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L2 licensee (sharer) detail (XOL-209 PR 5a). Opened from SiteSharers inside the
  // site drawer (stacked as L2: /sites/d.site.555.sharers/d.licensee.789).
  // GET /licensees/{id} appends `site` to the response, so siteId is NOT needed in
  // the URL — LicenseeDetailLayer synthesises rowData.options = data.site directly.
  // tab drives LicenseeDetails sub-tab deep-linking (slug map in LicenseeDetails.vue).
  licensee: {
    args: ['id', 'tab'],
    types: { id: 'int', tab: 'string' }
  },
  // L2 progress action detail (XOL-209 PR 5b). Opened from SiteProgress inside the
  // site drawer (stacked as L2: /sites/d.site.555.permits/d.progress.789).
  // GET /actions/{id} returns bare envelope; ProgressDetailLayer wraps ProgressWorkflow.
  // tab deep-links the ProgressWorkflow el-tabs (summary/step1..step4/landowner).
  progress: {
    args: ['id', 'tab'],
    types: { id: 'int', tab: 'string' }
  },
  // L2 permit detail (XOL-209 PR 5b). Opened from SitePermits inside the site drawer
  // (stacked as L2: /sites/d.site.555.permits/d.permit.789).
  // GET /permits/{id} returns bare envelope; PermitDetailLayer wraps PermitDetails.
  permit: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L2 candidate (RTA option) detail (XOL-209 PR 5b). Opened from SiteCandidates.
  // GET /candidate/{id} returns bare envelope with appended `site` — CandidateDetailLayer
  // synthesises rowData.options.siteData = data.site because AcquireDetails.data() reads
  // rowData.options.siteData. XOL-67 (B3): an RTA's options (site_id NULL until completion)
  // open it from the RTA drawer, so it stacks under `d.request.<id>` (RtaDetails Steps 2-3) as
  // well as under `d.site.<id>` (the completed site's read-only RTA record). The option rows
  // and this layer's content are rebuilt in B4.
  candidate: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 site-workflow detail (XOL-231 PR C). Self-contained via GET /workflow/{id}
  // (XOL-294): returns bare envelope with site/organisation/contact/percentComplete/
  // totalTasks/photos appended. Keyed by id only — no siteId needed in the URL.
  siteWorkflow: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 agreement detail (XOL-232 PR 6b-1). Opened from AgreementsTable (/finance/rates).
  // GET /agreements/{id} returns bare envelope with organisation + rates appended.
  // Keyed by id only — no orgId needed in the URL.
  // DISABLED as part of XOL-65 (finance Agreements hidden). Removing it from the
  // schema makes isKnownDrawer('agreement') false, so the parser drops a
  // d.agreement.<id> URL segment as unknown. Re-add to restore the drawer.
  // agreement: {
  //   args: ['id'],
  //   types: { id: 'int' }
  // },
  // L1/L2 contractor invoice detail (XOL-232 PR 6b-2). Opened from
  // ContractorInvoiceTable (L1, InvoicingView) and SiteContractorsTable (L2, site
  // finance drawer). GET /contractors/{id} returns bare envelope with site +
  // contractor appended. Keyed by id only — no siteId needed in the URL.
  siteContractor: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 site tenancy detail (XOL-233 PR 7b). Opened from SitesWithoutLandlords
  // (/dashboard/property). Keyed by site id — GET /site/{id} returns the full
  // site record; SiteTenancyDetailLayer passes it to <site-tenancy> which
  // self-fetches tenancies internally.
  siteTenancy: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 property-landlord detail (XOL-233 PR 7b). Opened from PropertyLandlords
  // (/dashboard/property/landlords). Keyed by owner_id — backs a nested renewals
  // list (NOT a single record) via GET /report/property/renewals/{ownerId}.
  propertyLandlord: {
    args: ['id'],
    types: { id: 'int' }
  },
  // Property detail (XOL-233). Opened from the nested "Sites By Landlord" table
  // inside the propertyLandlord drawer. Keyed by site id; PropertyDetailsLayer
  // assembles { site, tenancy } from GET /site/{id} + /tenancy/site/{id} and
  // renders <property-details> (site header + Tenancy / Location Map / Sharer).
  propertyDetails: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 site-equipment detail (XOL-234 PR 8). Opened from EquipView (/dashboard
  // /assets). Keyed by site id — GET /site/{id} returns the full site record;
  // SiteEquipViewLayer passes it straight to <site-equip-view>.
  siteEquip: {
    args: ['id'],
    types: { id: 'int' }
  },
  // L1 equipment (asset) detail (XOL-234 PR 8b). Opened from EquipTable.vue
  // (the editable per-site equipment table under Sites/Assets, telco-gated).
  // Keyed by ASSET id (not site id, unlike siteEquip above) — GET /assets/{id}
  // returns the asset with `site` + `organisation` appended.
  //
  // XOL-596 (completed steps): opened from a request's step 2 it also carries the step
  // context. `actionStepId` is the `action_steps.id` the equipment write is made from (the API
  // refuses the write while that step is completed) and `frozen` is 1 when the step was
  // completed when the drawer was opened (the drawer then offers no Edit). Both are OPTIONAL
  // ('optint': absent = null), so every `d.equipDetail.<id>` link that predates them still
  // parses, and a site page's equipment table (which sends no context) is unchanged.
  equipDetail: {
    args: ['id', 'actionStepId', 'frozen'],
    types: { id: 'int', actionStepId: 'optint', frozen: 'optint' }
  },
  // L1 read-only utility-bill report drilldown (XOL-397). Opened from
  // dashboard/utility/UtilityReport.vue (/dashboard/utility). Keyed by BILL id
  // (utility.id): each report row is one bill. GET /utility/{id} returns the full
  // bill with `sharers` + `site` appended, so no site/period arg is needed.
  // Read-only counterpart of `bill` (editable SiteUtilBillDetail).
  utilityReport: {
    args: ['id'],
    types: { id: 'int' }
  },

  // L1 organisation detail (XOL-235 A1). Opened from OrgTable.vue on the
  // Settings › orgs-* tabs (/settings/<tab>/d.org.<id>). Keyed by organisation id —
  // GET /org/{id} (OrganisationController@show) returns the bare model with the same
  // $appends as the list rows (parent, contactsCount, childrenCount, active, stats).
  org: {
    // `tab` (XOL-235 A2): the OrgDetail tab slug (map in src/lib/orgDetailTabs.js). Main is
    // omitted, so every A1 link (d.org.42) still parses (tab: null) and serialises unchanged.
    args: ['id', 'tab'],
    types: { id: 'int', tab: 'string' }
  },

  // L2 contact detail (XOL-235 A2). Opened from ContactsTable.vue inside the org drawer's
  // Contacts tab (/settings/<tab>/d.org.<id>.contacts/d.contact.<id>). Keyed by contact id —
  // GET /contacts/{id} (ContactsController@show) returns Contacts::removeProps($row), the same
  // shape as the list rows (orgCategory appended). Any Tower Company caller may read any
  // org's contact (xolas-tms-api #299); others own-org only (403).
  contact: {
    args: ['id'],
    types: { id: 'int' }
  },

  // L2 insurance policy detail (XOL-677 B1). Opened from InsuranceTable.vue inside the site
  // drawer's Insurance tab (/sites/d.site.<siteId>.ins/d.insurance.<id>). Keyed by insurance id —
  // GET /insurance/{id} (InsuranceController@show) returns the bare model with `provider` +
  // `beneficary` appended (the list rows' shape). A missing id is 404 {code:'not_found'} after
  // xolas-tms-api #304 (it was 200 {warning}); another org's record (non-Tower-Co caller) is 403.
  // No tab arg: InsuranceDetails' Statement tab is a stub; a string arg can be added later.
  insurance: {
    args: ['id'],
    types: { id: 'int' }
  },

  // L2 site-action detail (XOL-677 B2). Opened from ActionsTable.vue inside the site drawer's
  // Actions tab (/sites/d.site.<siteId>.actions.<active|rejected|closed>/d.action.<id>). Keyed by
  // action id — GET /actions/{id} (ActionsController@show), bare, with the same appends as the list
  // rows. Renders the editable ActionDetails form (read-only for a Closed / Rejected record). Not
  // `progress` (ProgressDetail, the PRG steps) and not `request` (RequestWorkflow): those are other
  // views of the same record. A missing id is 404 {code:'not_found'}; another org's action is 403.
  action: {
    args: ['id'],
    types: { id: 'int' }
  }
})

export function isKnownDrawer (name) {
  return Object.prototype.hasOwnProperty.call(drawerSchema, name)
}
